import { useRouter } from "next/navigation";
import { components } from "@zapier/api-clients/apis/templates-backend";
import { emitWebMcpToolInvocationEvent } from "src/observability/tracking";
import { useWebMcpTools, type WebMcpToolDefinition } from "src/utils/webmcp";

type Category = components["schemas"]["Category"];

const TOOL_NAME = "filterTemplatesByCategory";
// Shared surface tag for the /templates library WebMCP tools, mirroring how
// explore tags its surfaces (e.g. `apps_directory`) for the AGD-13 dashboard.
const WEBMCP_SURFACE = "templates_library";

const defaultResolveHref = (slug: string) => `/templates/${slug}`;

/**
 * Registers a WebMCP navigation tool so browser AI agents can filter
 * zapier.com/templates by category (AGD-28, epic AGD-25: WebMCP).
 *
 * Uses the shared `useWebMcpTools` wrapper (feature detection, the
 * late-injection watcher, result serialisation, teardown) and injects telemetry
 * via its `onInvoke` seam — the same pattern as the explore and marketing
 * surfaces — so the AGD-13 dashboard counts invocations across repos.
 *
 * We use the imperative API rather than the declarative form annotations from
 * AGD-27 because the /templates category filter is link-based navigation, not an
 * annotatable <form>. `resolveHref` maps a chosen category slug to the
 * destination URL, mirroring the page's own category links: landing/standard
 * category pages navigate to `/templates/{slug}` (the default), while
 * custom/gallery pages filter in place via `{pathname}?category={slug}`.
 *
 * @see https://developer.chrome.com/docs/ai/webmcp/imperative-api
 */
export const useWebMcpCategoryTool = (
  categories: Category[],
  resolveHref: (slug: string) => string = defaultResolveHref,
) => {
  const router = useRouter();

  const tools: WebMcpToolDefinition[] =
    categories.length === 0
      ? []
      : [
          {
            name: TOOL_NAME,
            description: "Filter Zapier templates by category.",
            inputSchema: {
              type: "object",
              properties: {
                category: {
                  type: "string",
                  description:
                    "The slug of the template category to filter by.",
                  enum: categories.map((category) => category.slug),
                },
              },
              required: ["category"],
            },
            execute: (input) => {
              const category =
                typeof input.category === "string" ? input.category : "";
              router.push(resolveHref(category));
              return `Showing templates for the ${category} category.`;
            },
          },
        ];

  useWebMcpTools(tools, {
    onInvoke: (toolName, outcome) =>
      emitWebMcpToolInvocationEvent(WEBMCP_SURFACE, toolName, outcome),
  });
};
